import * as React from 'react'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { F0Card } from '@factorialco/f0-react'
import { Calendar, Feed, Star } from '@factorialco/f0-react/icons/app'
import {
  PerformanceReviewEvaluationStatusEnum,
  type PerformanceReviewerStrategiesStrategyEnum,
} from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { ReplaceReviewerModal } from 'modules/performance_process/components/common/ReplaceReviewerModal'
import { getEvaluationType } from 'modules/performance_process/components/review/answer/helpers/getEvaluationType'
import { statusColor, tagLabel } from 'modules/performance_process/lib/answerTag'
import { getEvaluationDateLabel } from 'modules/performance_process/lib/evaluationDateLabel'

interface EvaluationCardBaseAvatar {
  fullName: string
  avatar?: string
  isYou: boolean
}

interface EvaluationCardBaseProps {
  participant: EvaluationCardBaseAvatar
  evaluationType: PerformanceReviewerStrategiesStrategyEnum
  status: PerformanceReviewEvaluationStatusEnum
  publishedAt: string | null | undefined
  republishedAt: string | null | undefined
  endsAt: string | null | undefined
  evaluationScore?: number | null
  onClick: () => void
  onRepublish?: () => void
  canReplaceReviewer: boolean
  evaluationId: number
  excludedAccessIds?: Set<number>
  hideStatus?: boolean
}

export const EvaluationCardBase: React.FC<EvaluationCardBaseProps> = ({
  participant,
  evaluationType,
  status,
  publishedAt,
  republishedAt,
  endsAt,
  evaluationScore,
  onClick,
  onRepublish,
  canReplaceReviewer,
  evaluationId,
  excludedAccessIds,
  hideStatus = false,
}) => {
  const [showModal, toggleShowModal] = useToggle()

  const { title: evaluationTypeTitle } = getEvaluationType(evaluationType)

  const cardMetadata = [
    !hideStatus && {
      icon: Feed,
      property: {
        type: 'status' as const,
        value: {
          label: tagLabel(status),
          status: statusColor(status),
        },
      },
    },
    {
      icon: Calendar,
      property: {
        type: 'text' as const,
        value: getEvaluationDateLabel({
          status,
          publishedAt,
          republishedAt,
          endsAt,
        }),
      },
    },
    evaluationScore && {
      icon: Star,
      property: {
        type: 'text' as const,
        value: `${i18n.t(
          'performance_process.reviews.target_datatable.final_score.title'
        )}: ${i18n.formatNumber(evaluationScore)}`,
      },
    },
  ].filter(Boolean)

  const actions = [
    onRepublish && {
      label: i18n.t('performance_process.reviews.evaluations.edit'),
      onClick: onRepublish,
    },
    canReplaceReviewer && {
      label: i18n.t('performance_process.reviews.evaluations.replace_reviewer'),
      onClick: toggleShowModal(true),
      critical: true,
    },
  ].filter(Boolean)

  const cardActions = actions.length > 0 ? actions : undefined

  const title = participant.isYou
    ? `${participant.fullName} (${i18n.t('performance_process.reviews.target_you')})`
    : participant.fullName

  const avatar = {
    type: 'person' as const,
    firstName: participant.fullName || '',
    lastName: '',
    src: participant.avatar ? buildUrl(participant.avatar) : undefined,
  }

  return (
    <Box data-testid={`evaluation-card-${evaluationType}`} height="full">
      <F0Card
        avatar={avatar}
        title={title}
        description={evaluationTypeTitle}
        onClick={onClick}
        aria-label={title}
        fullHeight
        metadata={cardMetadata}
        otherActions={cardActions}
      />
      {showModal && canReplaceReviewer && (
        <ReplaceReviewerModal
          onClose={toggleShowModal(false)}
          evaluationId={evaluationId}
          excludedAccessIds={excludedAccessIds}
          evaluationType={evaluationType}
        />
      )}
    </Box>
  )
}
